import { default as React } from 'react';
import { useState } from 'react';
import {
  useApi,
  configApiRef,
  alertApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningIcon from '@mui/icons-material/Warning';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  deleteButton: {
    marginTop: theme.spacing(2),
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    textTransform: 'none',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  iconOnlyButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    opacity: 0.5,
    '&:hover': {
      color: theme.palette.error.main,
      opacity: 1,
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  warningIcon: {
    color: theme.palette.error.main,
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
}));

interface UnregisterAppButtonProps {
  /** The entity to delete/unregister. */
  entity: Entity;
  variant?: 'text' | 'outlined' | 'contained';
  fullWidth?: boolean;
  /** Render as a small icon button without text (for card grids, table actions) */
  iconOnly?: boolean;
  /** Callback invoked after successful deletion */
  onDeleted?: () => void;
}

export const UnregisterAppButton = ({
  entity,
  variant = 'outlined',
  fullWidth = false,
  iconOnly = false,
  onDeleted,
}: UnregisterAppButtonProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);

  const [openDialog, setOpenDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const appName = entity.metadata.name;
  const appTitle = entity.metadata.title || appName;
  const appType =
    entity.metadata.annotations?.['microapp/appType'] || 'Registered';
  const isHostedApp = appType === 'Hosted';
  const hasPersistentStorage =
    entity.metadata.annotations?.['microapp/persistentStorage'] === 'true';
  const storageSize =
    entity.metadata.annotations?.['microapp/storageSize'] || '';

  const actionLabel = isHostedApp ? 'Delete' : 'Unregister';
  const actionLabelLower = isHostedApp ? 'delete' : 'unregister';
  const actionLabelProgress = isHostedApp ? 'Deleting...' : 'Unregistering...';

  const handleOpenDialog = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (!isDeleting) {
      setOpenDialog(false);
    }
  };

  const handleUnregister = async () => {
    setIsDeleting(true);

    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/microapps-api/unregister`;

      // Get authentication token
      const { token } = await identityApi.getCredentials();

      // Call unregister API - backend will handle GitHub deletion, ArgoCD cleanup, and catalog removal
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          appName,
          entityName: entity.metadata.name,
          entityKind: entity.kind,
          entityNamespace: entity.metadata.namespace || 'default',
          appType,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || result.message || `Failed to ${actionLabelLower} app`,
        );
      }

      alertApi.post({
        message: `Successfully ${
          isHostedApp ? 'deleted' : 'unregistered'
        } ${appTitle}. Refreshing catalog...`,
        severity: 'success',
        display: 'transient',
      });

      // Warn if any secrets could not be automatically deleted from GSM
      if (isHostedApp) {
        const failedSecrets: string[] = result.gcpSecretsDeletionFailed ?? [];
        if (failedSecrets.length > 0) {
          alertApi.post({
            message: `Warning: ${
              failedSecrets.length
            } secret(s) could not be automatically deleted from Google Secret Manager and require manual cleanup: ${failedSecrets.join(
              ', ',
            )}`,
            severity: 'warning',
          });
        } else if (
          result.gcpSecretsNote &&
          result.gcpSecretsNote.toLowerCase().includes('manual')
        ) {
          alertApi.post({
            message: `Warning: Could not automatically delete secrets from Google Secret Manager for app "${appName}". Please check GCP Secret Manager and remove secrets manually.`,
            severity: 'warning',
          });
        }
      }

      if (onDeleted) {
        // Use callback if provided (e.g., from catalog view to refresh list)
        setTimeout(() => {
          onDeleted();
        }, 1500);
      } else {
        // Default: redirect to apps page
        setTimeout(() => {
          window.location.href = '/apps';
        }, 1500);
      }
    } catch (error) {
      alertApi.post({
        message: `Failed to ${actionLabelLower} app: ${
          (error as Error).message
        }`,
        severity: 'error',
      });
      setIsDeleting(false);
      setOpenDialog(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip title={`${actionLabel} ${appTitle}`}>
          <IconButton
            className={classes.iconOnlyButton}
            size="small"
            onClick={handleOpenDialog}
            disabled={isDeleting}
            aria-label={`${actionLabelLower} ${appTitle}`}
          >
            {isDeleting ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteOutlineIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant={variant}
          size="small"
          startIcon={<DeleteIcon fontSize="small" />}
          onClick={handleOpenDialog}
          className={classes.deleteButton}
          fullWidth={fullWidth}
          disabled={isDeleting}
        >
          {actionLabel} App
        </Button>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        onClick={e => e.stopPropagation()}
        aria-labelledby="unregister-dialog-title"
        aria-describedby="unregister-dialog-description"
      >
        <DialogTitle id="unregister-dialog-title">
          <div className={classes.dialogTitle}>
            <WarningIcon className={classes.warningIcon} />
            Confirm App {actionLabel}
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unregister-dialog-description">
            Are you sure you want to {actionLabelLower}{' '}
            <strong>{appTitle}</strong>?
            <br />
            <br />
            This action will:
            <ul>
              {isHostedApp && (
                <>
                  <li>
                    Delete Kubernetes manifests from the hosted-projects repo
                  </li>
                  <li>Remove the ArgoCD application configuration</li>
                  <li>
                    ArgoCD will automatically clean up Kubernetes resources
                    (deployment, service, OAuth2 proxy)
                  </li>
                  <li>Delete all secrets from Google Secret Manager</li>
                </>
              )}
              {!isHostedApp && (
                <li>
                  Delete the registered app files from the hosted-projects repo
                </li>
              )}
              <li>Delete the catalog location</li>
              <li>Remove the app from the catalog</li>
              {hasPersistentStorage && (
                <li>
                  <strong style={{ color: '#d32f2f' }}>
                    Permanently delete all persistent storage data
                    {storageSize ? ` (${storageSize})` : ''}
                  </strong>
                </li>
              )}
            </ul>
            {hasPersistentStorage && (
              <>
                <br />
                <strong style={{ color: '#d32f2f' }}>
                  Warning: All data stored in /data (including databases, files,
                  and uploads) will be permanently deleted and cannot be
                  recovered. Make sure you have backed up any important data
                  before proceeding.
                </strong>
                <br />
              </>
            )}
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnregister}
            color="secondary"
            variant="contained"
            startIcon={
              isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />
            }
            disabled={isDeleting}
          >
            {isDeleting ? actionLabelProgress : actionLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
